// @flow

import { useCallback, useEffect, useRef } from 'react'

import { usePlaybackHeartbeat } from '@alphaott/app-analytics'

import { usePlayerContext } from '../store'

const getFirstSourceUrl = (mediaStreams: ?Array<Object>): string =>
  mediaStreams?.[0]?.sources?.[0]?.src || ''

type Params = {
  contentType: string,
  contentId: string,
  contentTitle: string,
  mediaStreams?: Array<Object>,
  channel?: ?{ id: string, title: string },
  tvShow?: ?{ id: string, title: string, seasonNumber: number, episodeNumber: number },
}

// eslint-disable-next-line complexity
export const useContentHeartbeat = ({
  contentType,
  contentId,
  contentTitle,
  mediaStreams,
  channel,
  tvShow,
}: Params): ({ onTimeUpdate: (time: { currentTime: number, duration: number }) => void }) => {
  const [state] = usePlayerContext()
  const { playing, seeking, loading, stopped, src } = state

  const progressRef = useRef({ position: 0, duration: 0, speed: 1 })

  const heartbeat = usePlaybackHeartbeat({
    contentType,
    contentId,
    contentTitle,
    getProgress: () => progressRef.current,
    channel,
    tvShow,
  })

  const contentUrl = src || getFirstSourceUrl(mediaStreams)
  useEffect(() => {
    if (contentUrl) {
      heartbeat.onSourceLoaded(contentUrl)
    }
  }, [contentUrl]) // eslint-disable-line react-hooks/exhaustive-deps

  const isActivePlaying = Boolean(playing && !seeking && !loading)
  useEffect(() => {
    heartbeat.onActivePlayingChange(isActivePlaying)
  }, [isActivePlaying]) // eslint-disable-line react-hooks/exhaustive-deps

  const prevStoppedRef = useRef(Boolean(stopped))
  useEffect(() => {
    if (stopped && !prevStoppedRef.current) {
      heartbeat.onEnd()
    }
    prevStoppedRef.current = Boolean(stopped)
  }, [stopped]) // eslint-disable-line react-hooks/exhaustive-deps

  const onTimeUpdate = useCallback(({ currentTime, duration }) => {
    progressRef.current = { position: currentTime || 0, duration: duration || 0, speed: 1 }
  }, [])

  return { onTimeUpdate }
}

export default useContentHeartbeat
